import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowCounterClockwise, Trash, Clock, CurrencyDollar, EyeSlash } from "@phosphor-icons/react";
import { money } from "@/components/finance/fin";
import { HoursCalc } from "./HoursCalc";
import { HOURS_CATS, INCOME_CATS, EXPENSE_CATS, SOURCE_LABEL, defaultRate } from "./budgetModel";

function SourceBadge({ line }) {
  const [txt, cls] = line.source === "manual" ? ["Manual", "bg-secondary text-muted-foreground"]
    : line.overridden ? ["Edited import", "bg-amber-500/15 text-amber-400"] : [`Imported · ${SOURCE_LABEL[line.source]}`, "bg-emerald-500/15 text-emerald-400"];
  return <span className={`text-[10px] uppercase tracking-wider font-semibold rounded px-1.5 py-0.5 whitespace-nowrap ${cls}`} data-testid={`budget-source-${line.id}`}>{txt}</span>;
}

export function BudgetLineRow({ line, rates, onChange, onRemove }) {
  const manual = line.source === "manual";
  const hours = line.calc?.method === "hours";
  const cats = line.section === "income" ? INCOME_CATS : EXPENSE_CATS;
  const setAmount = (v) => onChange(manual ? { ...line, amount: v } : { ...line, amount: v, overridden: Number(v) !== Number(line.imported_amount) });
  return (
    <div className="rounded-lg border border-border/60 bg-secondary/20 p-3 space-y-2 transition-colors hover:border-primary/40" data-testid={`budget-line-${line.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        {manual ? (
          <Input value={line.label} onChange={(e) => onChange({ ...line, label: e.target.value })} data-testid={`budget-label-${line.id}`} className="h-8 flex-1 min-w-[140px] bg-transparent border-transparent hover:border-input px-1 font-medium" />
        ) : <p className="flex-1 min-w-[140px] text-sm font-medium" data-testid={`budget-label-${line.id}`}>{line.label}</p>}
        <SourceBadge line={line} />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {manual && (
          <select value={line.category} onChange={(e) => onChange({ ...line, category: e.target.value, calc: HOURS_CATS.has(e.target.value) ? line.calc : null })} data-testid={`budget-category-${line.id}`}
            className="h-9 rounded-md border border-input bg-secondary/40 px-2 text-xs max-w-[170px]">
            {cats.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        )}
        <div className="flex-1 min-w-[150px]">
          {hours ? <p className="text-xs text-muted-foreground">Hours × hourly rate</p> : (
            <Input type="number" step="0.01" min="0" value={line.amount} placeholder="0.00" onChange={(e) => setAmount(e.target.value)} data-testid={`budget-amount-${line.id}`} className="h-9 bg-secondary/40 tabular-nums" />
          )}
        </div>
        {manual && HOURS_CATS.has(line.category) && (
          <Button size="sm" variant="ghost" title={hours ? "Enter a dollar amount" : "Enter hours × rate"} data-testid={`budget-mode-${line.id}`}
            onClick={() => onChange({ ...line, calc: hours ? null : defaultRate(rates, line.category) })}>
            {hours ? <CurrencyDollar className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
          </Button>
        )}
        {line.overridden && <Button size="sm" variant="ghost" title="Use imported amount" data-testid={`budget-revert-${line.id}`} onClick={() => onChange({ ...line, amount: line.imported_amount, overridden: false })}><ArrowCounterClockwise className="h-4 w-4" /></Button>}
        <Button size="sm" variant="ghost" title={manual ? "Delete" : "Hide from this month"} data-testid={`budget-remove-${line.id}`} onClick={onRemove}>
          {manual ? <Trash className="h-4 w-4 text-red-400" /> : <EyeSlash className="h-4 w-4 text-muted-foreground" />}
        </Button>
      </div>
      {hours && <HoursCalc line={line} rates={rates} onChange={onChange} />}
      {(line.meta || line.overridden) && (
        <p className="text-[11px] text-muted-foreground">
          {line.meta}{line.overridden && <span className="text-amber-400/90">{line.meta ? " · " : ""}Imported {money(line.imported_amount)} — your edit applies to this budget only; tracker records are unchanged.</span>}
        </p>
      )}
    </div>
  );
}
